import { useEffect, useRef, useState } from 'react'
import './BottomSheet.css'

/**
 * Reusable bottom sheet for mobile.
 * - Backdrop click closes.
 * - Drag handle: swipe down >= dismissThreshold px closes.
 * - Respects iOS safe-area-inset-bottom via CSS.
 * - Locks page scroll while open.
 */
export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  dismissThreshold = 80,
  className = '',
}) {
  const sheetRef = useRef(null)
  const [dragY, setDragY] = useState(0)
  const startY = useRef(null)

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const onPointerDown = (e) => {
    // Only start drag from the handle area (data-bottomsheet-handle).
    if (!e.target.closest('[data-bottomsheet-handle]')) return
    startY.current = e.clientY
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e) => {
    if (startY.current == null) return
    const dy = Math.max(0, e.clientY - startY.current)
    setDragY(dy)
  }

  const onPointerUp = () => {
    if (startY.current == null) return
    const dy = dragY
    startY.current = null
    if (dy >= dismissThreshold) {
      setDragY(0)
      onClose?.()
    } else {
      setDragY(0)
    }
  }

  return (
    <div
      className={`bottom-sheet-backdrop ${className}`}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Bottom sheet'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div
        ref={sheetRef}
        className="bottom-sheet"
        style={{ transform: dragY ? `translateY(${dragY}px)` : undefined }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="bottom-sheet-handle-area" data-bottomsheet-handle>
          <div className="bottom-sheet-handle" />
        </div>
        {title ? (
          <div className="bottom-sheet-header">
            <h2 className="bottom-sheet-title">{title}</h2>
            <button
              type="button"
              className="bottom-sheet-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="bottom-sheet-content">{children}</div>
      </div>
    </div>
  )
}
